import requestV2 from '@/service/request-v2'
import { normalizePageResult } from '@/service/adapters/page-result'
import type { PageQuery, PageResult } from '@/types/crud'

export interface DepositCustomerFilters {
  customerKeyword: string
  unionid: string
  businessSourceName: string
  deptName: string
  empName: string
  orderTime: string[]
  paymentChannel: string
  refunded: '' | 0 | 1
  balanceStatus: '' | 0 | 1
  productName: string
  status: '' | 0 | 10 | 20 | 30 | 40 | 50
}

export interface DepositCustomerRecord {
  avatar: string
  balanceAmount: number
  balanceStatus: number
  balanceTime: string
  businessSourceName: string
  customerName: string
  dealAmount: number
  depositAmount: number
  depositPaymentId: number
  depositTime: string
  deptName: string
  empName: string
  orderTime: string
  paymentChannel: string
  paymentStatusName: string
  productName: string
  refundAmount: number | null
  refundTime: string
  refunded: number | null
  sourceOrderId: number
  sourceType: number
  sourceTypeName: string
  transactionLinked: number
  unionid: string
}

export interface DepositCustomerStatistics {
  totalDepositCustomerCount: number
  unpaidBalanceCustomerCount: number
  unpaidBalanceRate: number
}

export interface DepositCustomerPageResult
  extends PageResult<DepositCustomerRecord> {
  statistics: DepositCustomerStatistics
}

function createParams(
  query: PageQuery<DepositCustomerFilters>,
  includePage: boolean
) {
  const { orderTime, ...filters } = query.filters
  return {
    ...filters,
    orderTimeBegin: orderTime?.[0],
    orderTimeEnd: orderTime?.[1],
    ...(includePage ? { pageNo: query.pageNo, pageSize: query.pageSize } : {})
  }
}

function normalizeStatistics(payload: unknown): DepositCustomerStatistics {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new Error('定金客户统计响应必须是对象')
  }

  const statistics = payload as Record<string, unknown>
  const toNumber = (value: unknown, label: string) => {
    const normalized =
      typeof value === 'string' && value.trim() !== '' ? Number(value) : value
    if (typeof normalized !== 'number' || !Number.isFinite(normalized)) {
      throw new Error(`${label}必须是数字`)
    }
    return normalized
  }

  const totalDepositCustomerCount = toNumber(
    statistics.totalDepositCustomerCount,
    '已交定金客户总数'
  )
  const unpaidBalanceCustomerCount = toNumber(
    statistics.unpaidBalanceCustomerCount,
    '未交尾款客户数'
  )
  const unpaidBalanceRate = toNumber(
    statistics.unpaidBalanceRate,
    '未交尾款客户占比'
  )

  if (
    !Number.isInteger(totalDepositCustomerCount) ||
    totalDepositCustomerCount < 0
  ) {
    throw new Error('已交定金客户总数必须是非负整数')
  }
  if (
    !Number.isInteger(unpaidBalanceCustomerCount) ||
    unpaidBalanceCustomerCount < 0
  ) {
    throw new Error('未交尾款客户数必须是非负整数')
  }
  return {
    totalDepositCustomerCount,
    unpaidBalanceCustomerCount,
    unpaidBalanceRate
  }
}

export async function fetchDepositCustomerPage(
  query: PageQuery<DepositCustomerFilters>,
  signal: AbortSignal
): Promise<DepositCustomerPageResult> {
  const payload = await requestV2.get<unknown>({
    url: '/main/realtimeDepositCustomer/queryByConditionWithAuth',
    params: createParams(query, true),
    signal
  })
  const page = normalizePageResult<DepositCustomerRecord>(payload)
  const statistics = normalizeStatistics(
    (payload as Record<string, unknown>).statistics
  )
  return { ...page, statistics }
}

export function exportDepositCustomers(
  query: PageQuery<DepositCustomerFilters>
) {
  return requestV2.get<Blob>({
    url: '/main/realtimeDepositCustomer/exportWithAuth',
    params: createParams(query, false),
    responseType: 'blob'
  })
}
